// client/src/lib/dexVisibilityStore.ts

import {
  DEX_CATEGORY_ORDER,
  DERIVED_VARIANTS,
  MANUAL_ONLY_VARIANTS,
  type PokedexVariant,
} from "./pogoPokedexVariantClassifier";

const STORAGE_KEY = "nexus.dexVisibility.v1";

/**
 * Single source of truth for the FULL pill order, identical to the legacy
 * FULL_PILL_ORDER local in Pokedex.tsx. Exposed here so the swipe helper and
 * settings page read the same canonical sequence.
 */
export const ALL_DEX_VARIANTS_ORDERED: readonly PokedexVariant[] = [
  ...DEX_CATEGORY_ORDER,
  // pokedex-complete-dex: the derived "Complete" rollup sits after Purified
  // (end of the scannable carousel) and before the manual-only custom dexes.
  ...DERIVED_VARIANTS,
  ...MANUAL_ONLY_VARIANTS,
];

/* ────────────────────────────────────────────────────────────────────────────
   Server-driven tab REORDER (admin dex-config editor).
   ----------------------------------------------------------------------------
   ALL_DEX_VARIANTS_ORDERED is the bundled default order. An admin can reorder
   the tabs from the portal; the dex-config syncer feeds the override here and
   every ordering consumer (tab bar, swipe nav) reads orderedVariants() instead
   of the raw const, so a reorder is live app-wide with no rebuild. Reordering
   never changes which variants exist or their visibility — only their sequence.
   ──────────────────────────────────────────────────────────────────────────── */
const BASE_ORDER_INDEX = new Map<PokedexVariant, number>(
  ALL_DEX_VARIANTS_ORDERED.map((v, i) => [v, i]),
);
let runtimeOrder: Map<PokedexVariant, number> | null = null;

/**
 * Apply an admin tab-order override (variant → position). Keys unknown to this
 * build are ignored; variants without a position keep their bundled slot after
 * the overridden ones. Passing an empty map clears the override. Re-emits the
 * visibility event so mounted tab bars re-render.
 */
export function setRuntimeDexOrder(order: Record<string, number>): void {
  const entries = Object.entries(order).filter(
    ([v, n]) => BASE_ORDER_INDEX.has(v as PokedexVariant) && Number.isFinite(n),
  );
  runtimeOrder = entries.length
    ? new Map(entries.map(([v, n]) => [v as PokedexVariant, n]))
    : null;
  emit();
}

/** The effective variant order: bundled default with the admin override applied. */
export function orderedVariants(): readonly PokedexVariant[] {
  if (!runtimeOrder) return ALL_DEX_VARIANTS_ORDERED;
  const ro = runtimeOrder;
  // Stable sort by override position; un-overridden variants fall after, in
  // their bundled relative order.
  return [...ALL_DEX_VARIANTS_ORDERED].sort((a, b) => {
    const ka = ro.has(a) ? ro.get(a)! : ALL_DEX_VARIANTS_ORDERED.length + BASE_ORDER_INDEX.get(a)!;
    const kb = ro.has(b) ? ro.get(b)! : ALL_DEX_VARIANTS_ORDERED.length + BASE_ORDER_INDEX.get(b)!;
    return ka - kb;
  });
}

/**
 * The "pokemon" (All) tab is the home of the Pokedex page and can never be
 * hidden. Settings UI must disable its toggle. The store enforces this
 * invariant defensively: setVisible("pokemon", false) is a no-op.
 */
export const LOCKED_ON_VARIANTS: readonly PokedexVariant[] = ["pokemon"];

export const VISIBILITY_EVENT = "nexus:dex-visibility-changed";

let cache: Set<PokedexVariant> | null = null;

function loadFromStorage(): Set<PokedexVariant> {
  if (cache) return cache;
  if (typeof window === "undefined") {
    cache = new Set(ALL_DEX_VARIANTS_ORDERED);
    return cache;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      cache = new Set(ALL_DEX_VARIANTS_ORDERED);
      return cache;
    }
    const parsed = JSON.parse(raw) as { hidden?: string[] };
    const hidden = new Set(parsed.hidden ?? []);
    cache = new Set(
      ALL_DEX_VARIANTS_ORDERED.filter((v) => !hidden.has(v)),
    );
    // Defensive: locked-on variants are ALWAYS visible regardless of storage.
    for (const v of LOCKED_ON_VARIANTS) cache.add(v);
    return cache;
  } catch {
    cache = new Set(ALL_DEX_VARIANTS_ORDERED);
    return cache;
  }
}

function persist(): void {
  if (typeof window === "undefined" || !cache) return;
  const hidden = ALL_DEX_VARIANTS_ORDERED.filter((v) => !cache!.has(v));
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ hidden }));
  } catch {
    // ignore quota errors
  }
}

function emit(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(VISIBILITY_EVENT));
}

export function getVisibleDexes(): Set<PokedexVariant> {
  return new Set(loadFromStorage());
}

export function isDexVisible(v: PokedexVariant): boolean {
  return loadFromStorage().has(v);
}

export function listVisibleDexesOrdered(): readonly PokedexVariant[] {
  const set = loadFromStorage();
  return orderedVariants().filter((v) => set.has(v));
}

export function setDexVisible(v: PokedexVariant, visible: boolean): void {
  // Locked-on variants cannot be hidden.
  if (!visible && LOCKED_ON_VARIANTS.includes(v)) return;
  const set = loadFromStorage();
  if (visible) set.add(v);
  else set.delete(v);
  persist();
  emit();
}

/**
 * Defensive utility: if a user somehow ended up with every dex hidden (e.g.
 * a future bug, or a storage hack), reset to all-on so the app never has an
 * empty pill row. Returns true if a reset was performed.
 */
export function ensureAtLeastOneVisible(): boolean {
  const set = loadFromStorage();
  if (set.size === 0) {
    for (const v of ALL_DEX_VARIANTS_ORDERED) set.add(v);
    persist();
    emit();
    return true;
  }
  return false;
}

/**
 * Like adjacentCategory in pogoPokedexVariantClassifier.ts, but skips any
 * hidden variants. If the user is currently on a hidden variant (shouldn't
 * happen, but defensive), this treats `current` as if it sat between its
 * neighbours and finds the nearest visible neighbour in `dir`.
 *
 * Clamps at the visible edges (no wrap) to match in-game carousel behaviour.
 * Returns `current` unchanged when no visible neighbour exists in `dir`.
 */
export function adjacentVisibleCategory(
  current: PokedexVariant,
  dir: 1 | -1,
  visibleOverride?: ReadonlySet<PokedexVariant>,
): PokedexVariant {
  const visible = visibleOverride ?? loadFromStorage();
  const order = orderedVariants();
  const visibleList = order.filter((v) => visible.has(v));
  if (visibleList.length === 0) return current;
  const idx = visibleList.indexOf(current);
  if (idx === -1) {
    // Current is hidden - fall back to first visible in dir.
    const fullIdx = order.indexOf(current);
    if (fullIdx === -1) return current;
    if (dir === 1) {
      for (let i = fullIdx + 1; i < order.length; i++) {
        if (visible.has(order[i])) return order[i];
      }
    } else {
      for (let i = fullIdx - 1; i >= 0; i--) {
        if (visible.has(order[i])) return order[i];
      }
    }
    return current;
  }
  const next = idx + dir;
  if (next < 0 || next >= visibleList.length) return current;
  return visibleList[next];
}

/** Test-only helper: reset the in-memory cache. */
export function __resetDexVisibilityCacheForTests(): void {
  cache = null;
}
